import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';

import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { SystemInfoService, SystemInfo } from '../../../services/system-info.service';
import { NotificationPollingService, ReminderToast } from '../../../services/notification-polling.service';
import { ReminderService } from '../../../services/reminder.service';
import { Reminder } from '../../../interfaces/reminder.interface';
import { VERSION } from '../../../../environments/version';
import { Subscription } from 'rxjs';
import { CallCenterDialogService } from '../../../services/call-center-dialog.service';
import { CallCenterApiService } from '../../../services/call-center-api.service';
import { CallCenterBusinessService } from '../../../services/call-center-business.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-header',
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    MatBadgeModule
],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('notificationTrigger') notificationTrigger!: MatMenuTrigger;
  searchTerm: string = '';
  currentUser: any = null;
  systemInfo: SystemInfo | null = null;
  appVersion = VERSION;
  pendingCount: Signal<number>;
  pendingReminders: Signal<Reminder[]>;
  visibleToasts: Signal<ReminderToast[]>;
  bellShaking = signal(false);
  private bellShakeInterval: ReturnType<typeof setInterval> | null = null;
  private userSubscription: Subscription = new Subscription();
  private systemInfoSubscription: Subscription = new Subscription();

  constructor(
    private authService: AuthService, 
    private router: Router,
    private systemInfoService: SystemInfoService,
    private notificationPollingService: NotificationPollingService,
    private reminderService: ReminderService,
    private cdr: ChangeDetectorRef,
    private callCenterDialogService: CallCenterDialogService,
    private callCenterApiService: CallCenterApiService,
    private businessService: CallCenterBusinessService,
    private snackBar: MatSnackBar
  ) {
    this.pendingCount = toSignal(this.notificationPollingService.pendingCount$, { initialValue: 0 });
    this.pendingReminders = toSignal(this.notificationPollingService.pendingReminders$, { initialValue: [] as Reminder[] });
    this.visibleToasts = toSignal(this.notificationPollingService.visibleToasts$, { initialValue: [] as ReminderToast[] });
  }

  ngOnInit(): void {
    this.userSubscription = this.authService.userData$.subscribe(userData => {
      this.currentUser = userData;
      if (userData) {
        this.notificationPollingService.startPolling();
      } else {
        this.notificationPollingService.stopPolling();
      }
    });

    // Rendszerinformációk betöltése
    this.systemInfoSubscription = this.systemInfoService.getSystemInfo().subscribe({
      next: (info) => {
        this.systemInfo = info;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Rendszerinformációk betöltése sikertelen:', error);
      }
    });

    // Percenkénti csengő animáció, ha van emlékeztető
    this.bellShakeInterval = setInterval(() => {
      if (this.pendingCount() > 0) {
        this.bellShaking.set(true);
        setTimeout(() => this.bellShaking.set(false), 700);
      }
    }, 60_000);
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.systemInfoSubscription.unsubscribe();
    if (this.bellShakeInterval) clearInterval(this.bellShakeInterval);
    this.notificationPollingService.stopPolling();
  }

  /**
   * Formázza a deploy időt olvasható formátumra
   */
  formatDeployTime(deployTime: string): string {
    if (!deployTime || deployTime === 'N/A') {
      return 'N/A';
    }
    // Localhost ellenőrzése
    if (deployTime === 'LOCAL_DEV') {
      return 'LOCALHOST';
    }
    try {
      const date = new Date(deployTime);
      return date.toLocaleString('hu-HU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return deployTime;
    }
  }

  /**
   * Rövid adatbázis név megjelenítése
   */
  getShortDatabaseName(): string {
    if (!this.systemInfo?.database_name) {
      return 'N/A';
    }
    const dbName = this.systemInfo.database_name;
    
    // Localhost ellenőrzés
    if (dbName.toLowerCase().includes('localhost') || this.systemInfo.environment === 'local') {
      return 'LOCAL';
    }
    // Ha tartalmaz "dev" szöveget, akkor dev környezet
    if (dbName.toLowerCase().includes('dev')) {
      return 'DEV';
    }
    // Ha "prod" vagy normál bfg_crm, akkor production
    if (dbName.toLowerCase().includes('prod') || dbName === 'bfg_crm') {
      return 'PROD';
    }
    return dbName;
  }

  /**
   * Környezet színe
   */
  getEnvironmentColor(): string {
    const env = this.getShortDatabaseName();
    if (env === 'LOCAL') {
      return '#2196f3'; // Kék - localhost
    }
    if (env === 'DEV') {
      return '#ff9800'; // Narancssárga
    }
    if (env === 'PROD') {
      return '#4caf50'; // Zöld
    }
    return '#9e9e9e'; // Szürke
  }

  onLogout(): void {
    this.authService.logout();
  }

  onProfile(): void {
    this.router.navigate(['/profile']);
  }

  getUserIcon(): string {
    if (!this.currentUser?.jobs || this.currentUser.jobs.length === 0) {
      return 'person'; // Default ikon
    }
    
    const roles = this.currentUser.jobs;
    
    // Adminisztrátor szerepkörök
    if (roles.includes('admin') || roles.includes('sales_manager') || roles.includes('deputy_ceo')) {
      return 'account_circle';
    }
    
    // Call center munkatárs
    if (roles.includes('call_center')) {
      return 'support_agent';
    }
    
    // Kiemelt ügyfélmenedzser
    if (roles.includes('kam')) {
      return 'person_pin';
    }
    
    // Befektetési divízió
    if (roles.includes('investment')) {
      return 'account_balance';
    }
    
    // Default: általános felhasználó ikon
    return 'person';
  }

  onReminderComplete(reminder: Reminder, event: Event): void {
    event.stopPropagation();
    this.reminderService.updateReminderStatus(reminder.id, 'completed').subscribe({
      next: () => this.notificationPollingService.refreshNow(),
      error: () => {}
    });
  }

  onReminderDismiss(reminder: Reminder, event: Event): void {
    event.stopPropagation();
    this.reminderService.updateReminderStatus(reminder.id, 'dismissed').subscribe({
      next: () => this.notificationPollingService.refreshNow(),
      error: () => {}
    });
  }

  onReminderClick(reminder: Reminder): void {
    if (!reminder.company_id) return;

    // Menü bezárása a dialog megnyitása előtt
    this.notificationTrigger?.closeMenu();

    this.callCenterApiService.reloadSingleElement(reminder.company_id).subscribe({
      next: (companyData) => {
        this.callCenterApiService.getCompanyLogs(companyData.id).subscribe({
          next: (companyLogs) => {
            const statusOptions = this.businessService.state.statusOptions;
            this.callCenterDialogService.openStatusDialog(companyData, companyLogs, statusOptions)
              .subscribe((result) => {
                if (result && result.confirmed && (result.saveChangesOnly || result.selectedStatus)) {
                  this.businessService.processDialogResult(companyData, result);
                }
              });
          },
          error: () => {
            const statusOptions = this.businessService.state.statusOptions;
            this.callCenterDialogService.openStatusDialog(companyData, [], statusOptions)
              .subscribe((result) => {
                if (result && result.confirmed && (result.saveChangesOnly || result.selectedStatus)) {
                  this.businessService.processDialogResult(companyData, result);
                }
              });
          }
        });
      },
      error: () => {
        this.snackBar.open('Nem sikerült betölteni a cég adatait', 'Bezár', { duration: 3000 });
      }
    });
  }

  dismissToast(reminderId: number): void {
    this.notificationPollingService.dismissToast(reminderId);
  }

  formatReminderTime(isoTime: string): string {
    try {
      return new Date(isoTime).toLocaleString('hu-HU', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoTime;
    }
  }
}
